import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { TablePagination } from "@/components/ui/table-pagination";
import { AffiliateUsageLimitsToolbar } from './AffiliateUsageLimitsToolbar';
import { BulkSetLimitsDialog, BulkResetDialog } from './BulkActionDialogs';
import { useAdminAffiliateLimits } from '@/hooks/useAffiliateUsageLimits';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Settings, Users, TrendingUp, AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

export const AdminAffiliateLimitsManagement = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { 
    allAffiliateLimits, 
    isLoading, 
    setAffiliateLimits,
    isUpdatingLimits,
    getTotalSystemUsage 
  } = useAdminAffiliateLimits();

  // Editing state
  const [editingLimits, setEditingLimits] = useState<{
    affiliateId: string;
    daily: number;
    monthly: number;
  } | null>(null);

  // Mobile expandable rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Selection state
  const [selectedAffiliates, setSelectedAffiliates] = useState<Set<string>>(new Set());

  // Bulk action dialogs
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    type: 'daily' | 'monthly' | 'reset' | null;
    open: boolean;
  }>({ type: null, open: false });

  const [debugInfo, setDebugInfo] = useState<string>('');

  const totalSystemUsage = getTotalSystemUsage();

  // Filter and search logic
  const filteredAffiliates = useMemo(() => {
    if (!allAffiliateLimits) return [];
    
    let filtered = allAffiliateLimits.filter(affiliate => {
      // Search filter
      const matchesSearch = !searchTerm || 
        affiliate.affiliateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        affiliate.affiliateEmail.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Usage filter
      const dailyUtilization = affiliate.dailyLimit > 0 ? 
        Math.round((affiliate.allocatedDaily / affiliate.dailyLimit) * 100) : 0;
      const monthlyUtilization = affiliate.monthlyLimit > 0 ? 
        Math.round((affiliate.allocatedMonthly / affiliate.monthlyLimit) * 100) : 0;
      const maxUtilization = Math.max(dailyUtilization, monthlyUtilization);
      
      let matchesFilter = true;
      switch (selectedFilter) {
        case 'high':
          matchesFilter = maxUtilization > 80;
          break;
        case 'medium':
          matchesFilter = maxUtilization >= 60 && maxUtilization <= 80;
          break;
        case 'low':
          matchesFilter = maxUtilization < 60;
          break;
        default:
          matchesFilter = true;
      }
      
      return matchesSearch && matchesFilter;
    });
    
    return filtered;
  }, [allAffiliateLimits, searchTerm, selectedFilter]);

  // Pagination logic
  const paginatedAffiliates = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAffiliates.slice(startIndex, startIndex + pageSize);
  }, [filteredAffiliates, currentPage, pageSize]);

  // Selection handlers
  const handleSelectAffiliate = (affiliateId: string, checked: boolean) => {
    const newSelected = new Set(selectedAffiliates);
    if (checked) {
      newSelected.add(affiliateId);
    } else {
      newSelected.delete(affiliateId);
    }
    setSelectedAffiliates(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedAffiliates(new Set(paginatedAffiliates.map(a => a.affiliateId)));
  };

  const handleDeselectAll = () => {
    setSelectedAffiliates(new Set());
  };

  const isAllSelected = paginatedAffiliates.length > 0 && 
    paginatedAffiliates.every(a => selectedAffiliates.has(a.affiliateId));

  // Bulk action handlers
  const handleBulkAction = (action: string) => {
    switch (action) {
      case 'setDailyLimits':
        setBulkActionDialog({ type: 'daily', open: true });
        break;
      case 'setMonthlyLimits':
        setBulkActionDialog({ type: 'monthly', open: true });
        break;
      case 'resetToDefaults':
        setBulkActionDialog({ type: 'reset', open: true });
        break;
      case 'export':
        handleExportSelected();
        break;
    }
  };

  const handleBulkSetLimits = async (daily: number, monthly: number) => {
    if (!user?.id) return;
    
    const selectedIds = Array.from(selectedAffiliates);
    
    for (const affiliateId of selectedIds) {
      await setAffiliateLimits({
        affiliateId,
        daily,
        monthly,
        adminId: user.id
      });
    }
    
    toast.success(`Updated limits for ${selectedIds.length} affiliates`);
    setBulkActionDialog({ type: null, open: false });
    setSelectedAffiliates(new Set());
  };

  const handleBulkReset = async () => {
    if (!user?.id) return;
    
    const selectedIds = Array.from(selectedAffiliates);
    
    for (const affiliateId of selectedIds) {
      await setAffiliateLimits({
        affiliateId,
        daily: 10,
        monthly: 100,
        adminId: user.id
      });
    }
    
    toast.success(`Reset limits for ${selectedIds.length} affiliates`);
    setBulkActionDialog({ type: null, open: false });
    setSelectedAffiliates(new Set());
  };

  const handleExportSelected = () => {
    const selectedIds = Array.from(selectedAffiliates);
    const dataToExport = allAffiliateLimits?.filter(a => selectedIds.includes(a.affiliateId)) || [];
    
    const csvContent = [
      ['Name', 'Email', 'Daily Limit', 'Monthly Limit', 'Daily Used', 'Monthly Used', 'Users'].join(','),
      ...dataToExport.map(a => [
        a.affiliateName,
        a.affiliateEmail,
        a.dailyLimit,
        a.monthlyLimit,
        a.allocatedDaily,
        a.allocatedMonthly,
        a.userCount
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate_limits_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${selectedIds.length} affiliates`);
  };

  useEffect(() => {
    setDebugInfo(`
      Loading: ${isLoading}
      Affiliate Count: ${allAffiliateLimits?.length || 0}
      User ID: ${user?.id || 'No user'}
    `);
  }, [isLoading, allAffiliateLimits, user]);

  const handleSetLimits = async (affiliateId: string, daily: number, monthly: number) => {
    if (!user?.id) return;
    
    setAffiliateLimits({
      affiliateId,
      daily,
      monthly,
      adminId: user.id
    });
    
    setEditingLimits(null);
  };

  const getUtilizationPercentage = (allocated: number, total: number) => {
    return total > 0 ? Math.round((allocated / total) * 100) : 0;
  };

  const toggleRowExpansion = (affiliateId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(affiliateId)) {
      newExpanded.delete(affiliateId);
    } else {
      newExpanded.add(affiliateId);
    }
    setExpandedRows(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Alert>
          <RefreshCw className="h-4 w-4 animate-spin" />
          <AlertDescription>
            Loading affiliate limits data... This may take a moment.
          </AlertDescription>
        </Alert>
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!allAffiliateLimits || allAffiliateLimits.length === 0) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No affiliate limits data found. This could be because:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>No affiliates exist in the system</li>
              <li>The migration script hasn't been run</li>
              <li>There's a database connection issue</li>
            </ul>
          </AlertDescription>
        </Alert>
        
        <Card>
          <CardHeader>
            <CardTitle>Debug Information</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded">
              {debugInfo}
            </pre>
            <p className="text-sm text-muted-foreground mt-2">
              Check the browser console for detailed debugging information.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Affiliates</p>
                <p className="text-2xl font-bold">{filteredAffiliates.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Daily Allocated</p>
                <p className="text-2xl font-bold">{totalSystemUsage.daily}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Monthly Allocated</p>
                <p className="text-2xl font-bold">{totalSystemUsage.monthly}</p>
              </div>
              <Settings className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Affiliate Limits Management */}
      <Card>
        <CardHeader>
          <CardTitle>Affiliate Usage Limits Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toolbar */}
          <AffiliateUsageLimitsToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
            selectedCount={selectedAffiliates.size}
            totalCount={filteredAffiliates.length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkAction={handleBulkAction}
            isAllSelected={isAllSelected}
          />

          {/* Mobile Card Layout */}
          {isMobile ? (
            <div className="space-y-4">
              {paginatedAffiliates.map((affiliate) => {
                const isEditing = editingLimits?.affiliateId === affiliate.affiliateId;
                const isExpanded = expandedRows.has(affiliate.affiliateId);
                const dailyUtilization = getUtilizationPercentage(affiliate.allocatedDaily, affiliate.dailyLimit);
                const monthlyUtilization = getUtilizationPercentage(affiliate.allocatedMonthly, affiliate.monthlyLimit);
                const isSelected = selectedAffiliates.has(affiliate.affiliateId);
                
                return (
                  <Card key={affiliate.affiliateId} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      {/* Header Row with Checkbox */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => 
                              handleSelectAffiliate(affiliate.affiliateId, checked as boolean)
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base truncate">{affiliate.affiliateName}</h3>
                            <p className="text-sm text-muted-foreground truncate">{affiliate.affiliateEmail}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRowExpansion(affiliate.affiliateId)}
                          className="ml-2 h-8 w-8 p-0"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </div>

                      {/* Key Stats Row */}
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Daily Limit</p>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="0"
                              value={editingLimits.daily}
                              onChange={(e) => setEditingLimits({
                                ...editingLimits,
                                daily: parseInt(e.target.value) || 0
                              })}
                              className="h-8 text-center"
                            />
                          ) : (
                            <Badge variant="secondary" className="text-sm">{affiliate.dailyLimit}</Badge>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Monthly Limit</p>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="0"
                              value={editingLimits.monthly}
                              onChange={(e) => setEditingLimits({
                                ...editingLimits,
                                monthly: parseInt(e.target.value) || 0
                              })}
                              className="h-8 text-center"
                            />
                          ) : (
                            <Badge variant="secondary" className="text-sm">{affiliate.monthlyLimit}</Badge>
                          )}
                        </div>
                      </div>

                      {/* Usage Progress */}
                      <div className="space-y-2 mb-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Daily Used: {affiliate.allocatedDaily}</span>
                            <span>{dailyUtilization}%</span>
                          </div>
                          <Progress value={dailyUtilization} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Monthly Used: {affiliate.allocatedMonthly}</span>
                            <span>{monthlyUtilization}%</span>
                          </div>
                          <Progress value={monthlyUtilization} className="h-2" />
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t pt-3 mt-3 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Users:</span>
                            <Badge variant="outline">{affiliate.userCount}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center justify-center gap-1 p-2 bg-muted rounded">
                              <span>{dailyUtilization >= 80 ? '🔴' : dailyUtilization >= 60 ? '🟡' : '🟢'}</span>
                              <span>Daily: {dailyUtilization}%</span>
                            </div>
                            <div className="flex items-center justify-center gap-1 p-2 bg-muted rounded">
                              <span>{monthlyUtilization >= 80 ? '🔴' : monthlyUtilization >= 60 ? '🟡' : '🟢'}</span>
                              <span>Monthly: {monthlyUtilization}%</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex justify-center mt-4">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSetLimits(
                                affiliate.affiliateId,
                                editingLimits.daily,
                                editingLimits.monthly
                              )}
                              disabled={isUpdatingLimits}
                              className="px-4"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingLimits(null)}
                              className="px-4"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingLimits({
                              affiliateId: affiliate.affiliateId,
                              daily: affiliate.dailyLimit,
                              monthly: affiliate.monthlyLimit
                            })}
                            className="px-4"
                          >
                            Edit Limits
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            /* Desktop Table Layout */
            <div className="w-full">
              <div 
                className="overflow-x-auto border rounded-md"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(155, 155, 155, 0.5) transparent'
                }}
              >
                <div className="min-w-[1200px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px] sticky left-0 bg-background z-10">
                          <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={isAllSelected ? handleDeselectAll : handleSelectAll}
                          />
                        </TableHead>
                        <TableHead className="w-[200px] sticky left-[50px] bg-background z-10 border-r">Affiliate</TableHead>
                        <TableHead className="w-[80px] text-center">Daily</TableHead>
                        <TableHead className="w-[80px] text-center">Monthly</TableHead>
                        <TableHead className="w-[120px] text-center">Daily Used</TableHead>
                        <TableHead className="w-[120px] text-center">Monthly Used</TableHead>
                        <TableHead className="w-[60px] text-center">Users</TableHead>
                        <TableHead className="w-[100px] text-center">Usage %</TableHead>
                        <TableHead className="w-[120px] text-center sticky right-0 bg-background z-10 border-l">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedAffiliates.map((affiliate) => {
                        const isEditing = editingLimits?.affiliateId === affiliate.affiliateId;
                        const dailyUtilization = getUtilizationPercentage(affiliate.allocatedDaily, affiliate.dailyLimit);
                        const monthlyUtilization = getUtilizationPercentage(affiliate.allocatedMonthly, affiliate.monthlyLimit);
                        const isSelected = selectedAffiliates.has(affiliate.affiliateId);
                        
                        return (
                          <TableRow key={affiliate.affiliateId}>
                            <TableCell className="sticky left-0 bg-background z-10">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => 
                                  handleSelectAffiliate(affiliate.affiliateId, checked as boolean)
                                }
                              />
                            </TableCell>
                            <TableCell className="sticky left-[50px] bg-background z-10 border-r">
                              <div className="min-w-0">
                                <div className="font-medium truncate">{affiliate.affiliateName}</div>
                                <div className="text-xs text-muted-foreground truncate">{affiliate.affiliateEmail}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="0"
                                  value={editingLimits.daily}
                                  onChange={(e) => setEditingLimits({
                                    ...editingLimits,
                                    daily: parseInt(e.target.value) || 0
                                  })}
                                  className="w-16 h-7 text-xs text-center"
                                />
                              ) : (
                                <Badge variant="secondary" className="text-xs">{affiliate.dailyLimit}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="0"
                                  value={editingLimits.monthly}
                                  onChange={(e) => setEditingLimits({
                                    ...editingLimits,
                                    monthly: parseInt(e.target.value) || 0
                                  })}
                                  className="w-16 h-7 text-xs text-center"
                                />
                              ) : (
                                <Badge variant="secondary" className="text-xs">{affiliate.monthlyLimit}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-xs font-medium">{affiliate.allocatedDaily}</span>
                                <Progress value={dailyUtilization} className="w-12 h-1" />
                                <span className="text-[10px] text-muted-foreground">{dailyUtilization}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-xs font-medium">{affiliate.allocatedMonthly}</span>
                                <Progress value={monthlyUtilization} className="w-12 h-1" />
                                <span className="text-[10px] text-muted-foreground">{monthlyUtilization}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-xs">{affiliate.userCount}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="space-y-1">
                                <div className="text-[10px] flex items-center justify-center gap-1">
                                  <span>{dailyUtilization >= 80 ? '🔴' : dailyUtilization >= 60 ? '🟡' : '🟢'}</span>
                                  <span>D:{dailyUtilization}%</span>
                                </div>
                                <div className="text-[10px] flex items-center justify-center gap-1">
                                  <span>{monthlyUtilization >= 80 ? '🔴' : monthlyUtilization >= 60 ? '🟡' : '🟢'}</span>
                                  <span>M:{monthlyUtilization}%</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center sticky right-0 bg-background z-10 border-l">
                              {isEditing ? (
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    size="sm"
                                    onClick={() => handleSetLimits(
                                      affiliate.affiliateId,
                                      editingLimits.daily,
                                      editingLimits.monthly
                                    )}
                                    disabled={isUpdatingLimits}
                                    className="h-7 px-2 text-xs"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingLimits(null)}
                                    className="h-7 px-2 text-xs"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingLimits({
                                    affiliateId: affiliate.affiliateId,
                                    daily: affiliate.dailyLimit,
                                    monthly: affiliate.monthlyLimit
                                  })}
                                  className="h-7 px-2 text-xs"
                                >
                                  Edit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              <div className="mt-4">
                <p className="text-xs text-muted-foreground text-center mb-2">
                  💡 Scroll horizontally to view all columns
                </p>
              </div>
            </div>
          )}

          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={filteredAffiliates.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* Bulk Action Dialogs */}
      <BulkSetLimitsDialog
        open={bulkActionDialog.type === 'daily' && bulkActionDialog.open}
        onOpenChange={(open) => setBulkActionDialog({ type: 'daily', open })}
        selectedCount={selectedAffiliates.size}
        onConfirm={handleBulkSetLimits}
        title="Set Bulk Daily Limits"
        isLoading={isUpdatingLimits}
      />

      <BulkSetLimitsDialog
        open={bulkActionDialog.type === 'monthly' && bulkActionDialog.open}
        onOpenChange={(open) => setBulkActionDialog({ type: 'monthly', open })}
        selectedCount={selectedAffiliates.size}
        onConfirm={handleBulkSetLimits}
        title="Set Bulk Monthly Limits"
        isLoading={isUpdatingLimits}
      />

      <BulkResetDialog
        open={bulkActionDialog.type === 'reset' && bulkActionDialog.open}
        onOpenChange={(open) => setBulkActionDialog({ type: 'reset', open })}
        selectedCount={selectedAffiliates.size}
        onConfirm={handleBulkReset}
        isLoading={isUpdatingLimits}
      />
    </div>
  );
};
